import { useEffect, useState } from "react";
import { Trash2, AlertTriangle, X, ChevronLeft, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { adminGetProgress, adminGetStudentDetail, adminDeleteStudentResult } from "../services/api";

// ── Helpers ───────────────────────────────────────────────────────
function pct(correct, total) {
  if (!total || Number(total) === 0) return 0;
  return Math.round((Number(correct) / Number(total)) * 100);
}

function PctBar({ value }) {
  const color = value >= 70 ? "#27ae60" : value >= 40 ? "#e67e22" : value > 0 ? "#c0392b" : "#dde6e6";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:".4rem" }}>
      <div style={{ flex:1, height:6, background:"#eef2f2", borderRadius:3, overflow:"hidden", minWidth:60 }}>
        <div style={{ width:`${value}%`, height:"100%", background:color, borderRadius:3, transition:"width .5s" }} />
      </div>
      <span style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.7rem", color, minWidth:28, textAlign:"right" }}>{value}%</span>
    </div>
  );
}

// ── Modal de confirmación ─────────────────────────────────────────
function ConfirmModal({ title, description, highlight, onConfirm, onCancel, loading }) {
  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:700,
      display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem",
    }}>
      <div style={{
        background:"var(--white)", borderRadius:"var(--card-radius)", padding:"2rem 1.8rem",
        maxWidth:420, width:"100%", boxShadow:"0 8px 32px rgba(0,0,0,0.18)",
        border:"0.5px solid var(--border)",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.2rem" }}>
          <div style={{
            width:48, height:48, borderRadius:"50%", background:"#fde8e8",
            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
          }}>
            <AlertTriangle size={22} color="#c0392b" strokeWidth={2} />
          </div>
          <button onClick={onCancel} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--text-muted)", padding:4 }}>
            <X size={18} />
          </button>
        </div>
        <h3 style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"1rem", color:"var(--charcoal)", marginBottom:".5rem" }}>
          {title}
        </h3>
        <p style={{ fontFamily:"var(--font-body)", fontSize:"0.82rem", color:"var(--text-muted)", marginBottom:".8rem" }}>
          {description}
        </p>
        {highlight && (
          <p style={{
            fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.88rem",
            color:"var(--teal-dark)", background:"var(--teal-light)",
            padding:".5rem .9rem", borderRadius:8, marginBottom:"1.2rem", wordBreak:"break-word",
          }}>
            {highlight}
          </p>
        )}
        <p style={{ fontFamily:"var(--font-body)", fontSize:"0.75rem", color:"#c0392b", marginBottom:"1.4rem" }}>
          Esta acción no se puede deshacer.
        </p>
        <div style={{ display:"flex", gap:".7rem", justifyContent:"flex-end" }}>
          <button className="btn-outline" onClick={onCancel} disabled={loading} style={{ minWidth:90 }}>
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              display:"flex", alignItems:"center", gap:".4rem",
              background:"#c0392b", color:"#fff", border:"none",
              borderRadius:6, padding:".55rem 1.1rem",
              fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.82rem",
              cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1,
              minWidth:90,
            }}
          >
            <Trash2 size={14} strokeWidth={2.5} />
            {loading ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── StudentDetail ─────────────────────────────────────────────────
function StudentDetail({ studentId, onBack, onResponseDeleted }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  // Confirm para borrar una clase específica
  const [confirm,  setConfirm]  = useState(null); // { classId, classTitle } | "all"
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    adminGetStudentDetail(studentId).then(setData).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [studentId]);

  const handleDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      if (confirm === "all") {
        // Borrar todas las clases respondidas
        const attempted = data.classes.filter(c => c.attempted && c.class_id);
        for (const c of attempted) {
          await adminDeleteStudentResult(c.class_id, studentId);
        }
      } else {
        await adminDeleteStudentResult(confirm.classId, studentId);
      }
      setConfirm(null);
      load();
      if (onResponseDeleted) onResponseDeleted();
    } catch (err) {
      alert("Error eliminando: " + err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <p style={{ color:"var(--text-muted)", fontSize:"0.85rem" }}>Cargando...</p>;
  if (!data)   return <p style={{ color:"#c0392b" }}>Error al cargar.</p>;

  const { user, classes } = data;
  const totalCorrect  = classes.reduce((s,c) => s + Number(c.correct||0), 0);
  const totalAnswered = classes.reduce((s,c) => s + Number(c.answered||0), 0);
  const score = pct(totalCorrect, totalAnswered);
  const hasAnyActivity = classes.some(c => c.attempted);

  const confirmData = confirm === "all"
    ? { title:"¿Eliminar todas las respuestas?", description:"Estás por eliminar todas las respuestas de:", highlight: user.name }
    : confirm
    ? { title:"¿Eliminar respuestas?", description:"Estás por eliminar todas las respuestas de este alumno en:", highlight: confirm.classTitle }
    : null;

  return (
    <div>
      {confirm && confirmData && (
        <ConfirmModal
          {...confirmData}
          loading={deleting}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}

      <div style={{ display:"flex", alignItems:"center", gap:"1rem", marginBottom:"1.5rem", flexWrap:"wrap" }}>
        <button className="btn-outline" style={{ fontSize:"0.75rem", display:"flex", alignItems:"center", gap:".3rem" }} onClick={onBack}>
          <ChevronLeft size={14} /> Volver
        </button>
        <div style={{ flex:1 }}>
          <h2 style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"1.1rem", color:"var(--charcoal)" }}>{user.name}</h2>
          <p style={{ fontSize:"0.78rem", color:"var(--text-muted)", display:"flex", gap:".6rem", flexWrap:"wrap", alignItems:"center" }}>
            {user.code && <span style={{ fontFamily:"var(--font-display)", fontWeight:700, color:"var(--teal)" }}>{user.code}</span>}
            <span>{user.email}</span>
            {user.section_name && (
              <span style={{ background:"var(--teal-light)", color:"var(--teal-dark)", fontFamily:"var(--font-display)", fontWeight:600, fontSize:"0.68rem", padding:"2px 8px", borderRadius:20 }}>
                {user.section_name}
              </span>
            )}
          </p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:"1rem", flexWrap:"wrap" }}>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"1.8rem", color:"var(--teal)" }}>{score}%</div>
            <div style={{ fontFamily:"var(--font-display)", fontSize:"0.65rem", color:"var(--text-muted)", textTransform:"uppercase" }}>Acierto global</div>
          </div>
          {hasAnyActivity && (
            <button
              onClick={() => setConfirm("all")}
              style={{
                display:"flex", alignItems:"center", gap:".4rem",
                background:"#fde8e8", color:"#c0392b",
                border:"0.5px solid #e8b0b0", borderRadius:6,
                padding:"7px 12px", fontSize:"0.75rem",
                fontFamily:"var(--font-display)", fontWeight:700,
                cursor:"pointer", whiteSpace:"nowrap",
              }}
            >
              <Trash2 size={14} strokeWidth={2.5} />
              Eliminar todo
            </button>
          )}
        </div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:".7rem" }}>
        {classes.map((c) => {
          const hasQuiz   = !!c.quiz_id;
          const attempted = c.attempted;
          const classPct  = pct(Number(c.correct||0), Number(c.answered||0));
          return (
            <div
              key={c.class_id}
              style={{
                background:"var(--white)", borderRadius:"var(--card-radius)",
                border:`0.5px solid ${!hasQuiz?"var(--border)":attempted?"var(--border)":"#f0c4a8"}`,
                borderLeft:`4px solid ${!hasQuiz?"var(--border)":attempted?"var(--teal)":"#e67e22"}`,
                padding:".9rem 1.1rem",
              }}
            >
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:".5rem", flexWrap:"wrap" }}>
                <div style={{ flex:1 }}>
                  <p style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.85rem", marginBottom:".2rem" }}>{c.class_title}</p>
                  {!hasQuiz && <span style={{ fontSize:"0.7rem", color:"var(--text-light)", fontStyle:"italic" }}>Sin cuestionario</span>}
                  {hasQuiz && !attempted && <span style={{ fontSize:"0.7rem", fontFamily:"var(--font-display)", fontWeight:700, background:"#fef3e7", color:"#e67e22", padding:"2px 8px", borderRadius:20 }}>No respondido</span>}
                  {hasQuiz && attempted && <span style={{ fontSize:"0.7rem", color:"var(--text-muted)" }}>{c.quiz_title} · {c.total_questions} preguntas</span>}
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:".8rem", flexWrap:"wrap" }}>
                  {hasQuiz && attempted && (
                    <div style={{ display:"flex", gap:"1rem" }}>
                      {[["✓",c.correct,"#27ae60"],["✗",c.wrong,"#c0392b"],["—",c.empty,"#8a9baa"]].map(([lbl,val,col]) => (
                        <div key={lbl} style={{ textAlign:"center" }}>
                          <div style={{ fontFamily:"var(--font-display)", fontWeight:800, fontSize:"1.1rem", color:col }}>{val}</div>
                          <div style={{ fontFamily:"var(--font-display)", fontSize:"0.6rem", color:"var(--text-muted)" }}>{lbl}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {hasQuiz && attempted && (
                    <button
                      onClick={() => setConfirm({ classId: c.class_id, classTitle: c.class_title })}
                      title="Eliminar respuestas"
                      style={{
                        display:"flex", alignItems:"center", gap:".35rem",
                        background:"#fde8e8", color:"#c0392b",
                        border:"0.5px solid #e8b0b0", borderRadius:6,
                        padding:"5px 10px", fontSize:"0.72rem",
                        fontFamily:"var(--font-display)", fontWeight:700,
                        cursor:"pointer",
                      }}
                    >
                      <Trash2 size={13} strokeWidth={2.5} />
                      Eliminar respuestas
                    </button>
                  )}
                </div>
              </div>
              {hasQuiz && attempted && <div style={{ marginTop:".5rem" }}><PctBar value={classPct} /></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── StudentStats (tabla principal) ────────────────────────────────
export default function StudentStats() {
  const [rows,      setRows]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [search,    setSearch]    = useState("");
  const [filterSec, setFilterSec] = useState("");
  const [sortKey,   setSortKey]   = useState("name");
  const [sortAsc,   setSortAsc]   = useState(true);

  const loadRows = () => {
    setLoading(true);
    adminGetProgress().then(setRows).finally(() => setLoading(false));
  };

  useEffect(() => { loadRows(); }, []);

  if (selected) {
    return (
      <StudentDetail
        studentId={selected}
        onBack={() => setSelected(null)}
        onResponseDeleted={loadRows}
      />
    );
  }

  const sections = [...new Set(rows.filter((r) => r.section_name).map((r) => r.section_name))];

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc((a) => !a);
    else { setSortKey(key); setSortAsc(true); }
  };

  const filtered = rows
    .filter((r) => {
      const q   = search.toLowerCase();
      const hit = !search || r.name.toLowerCase().includes(q) || (r.code||"").includes(q) || (r.email||"").toLowerCase().includes(q);
      const sec = !filterSec || r.section_name === filterSec;
      return hit && sec;
    })
    .sort((a, b) => {
      if (sortKey === "score") {
        const va = pct(Number(a.correct||0), Number(a.total_responses||0));
        const vb = pct(Number(b.correct||0), Number(b.total_responses||0));
        return sortAsc ? va - vb : vb - va;
      }
      if (sortKey === "quizzes") {
        return sortAsc
          ? Number(a.quizzes_taken||0) - Number(b.quizzes_taken||0)
          : Number(b.quizzes_taken||0) - Number(a.quizzes_taken||0);
      }
      const map = { name:"name", code:"code", section:"section_name" };
      const va = (a[map[sortKey]]||"").toLowerCase();
      const vb = (b[map[sortKey]]||"").toLowerCase();
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ?  1 : -1;
      return 0;
    });

  const withActivity = filtered.filter((r) => Number(r.quizzes_taken) > 0);
  const noActivity   = filtered.filter((r) => Number(r.quizzes_taken) === 0);

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ArrowUpDown size={11} style={{ opacity:.4 }} />;
    return sortAsc ? <ArrowUp size={11} /> : <ArrowDown size={11} />;
  };

  const SortTh = ({ k, label, align = "left" }) => (
    <th
      onClick={() => handleSort(k)}
      style={{
        fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.7rem",
        color: sortKey===k ? "var(--teal-dark)" : "var(--text-muted)",
        textAlign:align, padding:"8px 10px", textTransform:"uppercase", letterSpacing:.5,
        cursor:"pointer", whiteSpace:"nowrap", userSelect:"none",
        background: sortKey===k ? "var(--teal-light)" : "transparent",
      }}
    >
      <span style={{ display:"inline-flex", alignItems:"center", gap:".3rem" }}>
        {label} <SortIcon k={k} />
      </span>
    </th>
  );

  return (
    <div>
      <div className="admin-toolbar">
        <h2>Progreso de alumnos ({rows.length})</h2>
      </div>

      <div style={{ display:"flex", gap:".6rem", marginBottom:"1.2rem", flexWrap:"wrap" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar nombre, código o email..."
          style={{
            fontFamily:"var(--font-body)", fontSize:"0.82rem", padding:"7px 12px",
            border:"1px solid var(--border)", borderRadius:6, outline:"none", flex:1, minWidth:180,
          }}
        />
        <select
          value={filterSec}
          onChange={(e) => setFilterSec(e.target.value)}
          style={{
            fontFamily:"var(--font-body)", fontSize:"0.82rem", padding:"7px 10px",
            border:"1px solid var(--border)", borderRadius:6, outline:"none",
          }}
        >
          <option value="">Todas las secciones</option>
          {sections.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading && <p style={{ color:"var(--text-muted)", fontSize:"0.85rem" }}>Cargando...</p>}
      {!loading && filtered.length === 0 && <p className="admin-empty">No se encontraron alumnos.</p>}

      {withActivity.length > 0 && (
        <>
          <p style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.7rem", color:"var(--teal)", letterSpacing:.8, textTransform:"uppercase", marginBottom:".5rem" }}>
            Con actividad ({withActivity.length})
          </p>
          <div style={{ overflowX:"auto", marginBottom:"1.5rem" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.82rem" }}>
              <thead>
                <tr style={{ borderBottom:"1.5px solid var(--border)", background:"var(--teal-light)" }}>
                  <SortTh k="code"    label="Código" />
                  <SortTh k="name"    label="Nombre" />
                  <SortTh k="section" label="Sección" />
                  <SortTh k="score"   label="% Acierto" />
                  <SortTh k="quizzes" label="Quizzes" align="center" />
                  <th style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.7rem", color:"var(--text-muted)", padding:"8px 10px", textTransform:"uppercase", letterSpacing:.5, textAlign:"center" }}>
                    Correctas / Incorrectas / Sin resp.
                  </th>
                  <th style={{ padding:"8px 10px", width:40 }}></th>
                </tr>
              </thead>
              <tbody>
                {withActivity.map((r) => {
                  const score = pct(Number(r.correct||0), Number(r.total_responses||0));
                  return (
                    <tr
                      key={r.id}
                      style={{ borderBottom:"0.5px solid var(--border)" }}
                      onMouseEnter={(e) => e.currentTarget.style.background="var(--off-white)"}
                      onMouseLeave={(e) => e.currentTarget.style.background=""}
                    >
                      <td style={{ padding:"8px 10px", fontFamily:"var(--font-display)", fontWeight:700, color:"var(--teal-dark)", whiteSpace:"nowrap" }}>
                        {r.code || <span style={{ color:"var(--text-light)", fontWeight:400 }}>—</span>}
                      </td>
                      <td style={{ padding:"8px 10px", fontWeight:500, cursor:"pointer", color:"var(--teal)" }}
                        onClick={() => setSelected(r.id)}>
                        {r.name}
                      </td>
                      <td style={{ padding:"8px 10px" }}>
                        {r.section_name
                          ? <span style={{ background:"var(--teal-light)", color:"var(--teal-dark)", fontFamily:"var(--font-display)", fontWeight:600, fontSize:"0.68rem", padding:"2px 8px", borderRadius:20 }}>{r.section_name}</span>
                          : <span style={{ color:"var(--text-light)", fontSize:"0.75rem" }}>—</span>}
                      </td>
                      <td style={{ padding:"8px 10px", minWidth:120 }}><PctBar value={score} /></td>
                      <td style={{ padding:"8px 10px", textAlign:"center", fontFamily:"var(--font-display)", fontWeight:700, color:"var(--blue)" }}>
                        {r.quizzes_taken}
                      </td>
                      <td style={{ padding:"8px 10px", whiteSpace:"nowrap", textAlign:"center" }}>
                        <span style={{ color:"#27ae60", fontWeight:700 }}>{r.correct}</span>
                        <span style={{ color:"var(--text-light)", margin:"0 4px" }}>/</span>
                        <span style={{ color:"#c0392b", fontWeight:700 }}>{r.wrong}</span>
                        <span style={{ color:"var(--text-light)", margin:"0 4px" }}>/</span>
                        <span style={{ color:"#8a9baa", fontWeight:700 }}>{r.empty}</span>
                      </td>
                      <td style={{ padding:"8px 10px", color:"var(--teal)", cursor:"pointer" }}
                        onClick={() => setSelected(r.id)}>
                        <ChevronLeft size={16} style={{ transform:"rotate(180deg)" }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {noActivity.length > 0 && (
        <>
          <p style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"0.7rem", color:"#e67e22", letterSpacing:.8, textTransform:"uppercase", marginBottom:".5rem" }}>
            Sin actividad ({noActivity.length})
          </p>
          <div className="admin-list">
            {noActivity.map((r) => (
              <div
                key={r.id}
                className="admin-item"
                style={{ cursor:"pointer", borderLeft:"4px solid #f0c4a8" }}
                onClick={() => setSelected(r.id)}
                onMouseEnter={(e) => e.currentTarget.style.borderColor="#e67e22"}
                onMouseLeave={(e) => e.currentTarget.style.borderColor=""}
              >
                <div className="admin-item-info">
                  <span className="admin-item-title">
                    {r.code && (
                      <span style={{ fontFamily:"var(--font-display)", color:"var(--teal)", marginRight:".6rem", fontSize:"0.78rem" }}>{r.code}</span>
                    )}
                    {r.name}
                  </span>
                  <span style={{ fontSize:"0.75rem", color:"var(--text-muted)" }}>{r.email}</span>
                  {r.section_name && (
                    <span style={{ background:"var(--teal-light)", color:"var(--teal-dark)", fontFamily:"var(--font-display)", fontWeight:600, fontSize:"0.65rem", padding:"2px 8px", borderRadius:20, width:"fit-content" }}>
                      {r.section_name}
                    </span>
                  )}
                </div>
                <span style={{ fontSize:"0.72rem", fontFamily:"var(--font-display)", fontWeight:700, background:"#fef3e7", color:"#e67e22", padding:"3px 10px", borderRadius:20, flexShrink:0 }}>
                  Sin respuestas
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}